import * as React from 'react';
import { useAppDispatch, useTypedSelector } from '../../combineReducers';
import { useParams } from 'react-router-dom';
import {
  Paper,
  Grid,
  Button,
  Tooltip,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@material-ui/core';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { PARTNER_LOGOS } from '../../helpers/partner';
import { postDataSharingConsent } from '../../slices/dataSharingConsent';
import { Info } from '@material-ui/icons';
import { formatDate } from '../../helpers/formatDate';
import { useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { enqueueRequestErrorSnackbar } from '@pec/aion-ui-core/actions/enqueueSnackbar';
import { useTranslation } from 'react-i18next';

export const TestLocators = {
  thirdPartyName: 'title',
  logo: 'logo',
  description: 'description',
  info: 'info',
  localisation: 'localisation'
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      minHeight: 220,
      position: 'relative',
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2)
    },
    contentContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'top'
    },
    body: {
      marginTop: theme.spacing(3)
    },
    containerDisabled: {
      opacity: 0.5
    },
    title: {
      color: 'var(--on-surface-high-emphasis-000000-87-)',
      fontSize: 19,
      lineHeight: '24px',
      fontWeight: theme.typography.fontWeightRegular,
      letterSpacing: 0.18
    },
    button: {
      letterSpacing: 1.25,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      marginRight: theme.spacing(2)
    }
  })
);

type RouteParams = {
  organizationId: string;
};

type props = {
  logo: string;
  thirdPartyName: string;
  id: string;
  description: string;
  buttons: {
    label: string;
  }[];
};

export const Connections: React.FC<props> = ({ logo, thirdPartyName, id, description, buttons }) => {
  const { t } = useTranslation('subscriber');
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<RouteParams>();
  const { dataSharingConsent } = useTypedSelector(state => state.dataSharingConsent);
  const Logo = PARTNER_LOGOS[logo];
  const thirdParty = dataSharingConsent.find(el => el.thirdPartyOrganizationId === id);
  const [dataSharingActive, setDataSharingActive] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isFetching } = useTypedSelector(state => state.dataSharingConsent);
  const [open, setOpen] = React.useState(false);
  const handleClose = () => setOpen(false);

  React.useEffect(() => {
    isFetching ? setIsLoading(true) : setIsLoading(false);
  }, [isFetching]);

  React.useEffect(() => {
    if (thirdParty) thirdParty.isDataSharing ? setDataSharingActive('Active') : setDataSharingActive('Inactive');
  }, [thirdParty]);

  const toolTipText = () => {
    return (
      <Box>
        <Typography data-testid={TestLocators.info} variant={'body2'}>
          {thirdParty?.name}
          <br />
          {t('connections.previouslyAdded', 'Previously Added')}:
          <br />
          {thirdParty && thirdParty.dateConsentedUtc ? formatDate(thirdParty.dateConsentedUtc.toString()) : ''}
          <br /> {t('connections.previouslyRemoved', 'Previously Removed')}:
          <br />{' '}
          {thirdParty && !thirdParty.isDataSharing && thirdParty.dateConsentedUtc
            ? formatDate(thirdParty.dateConsentRemovedUtc.toString())
            : ''}
        </Typography>
      </Box>
    );
  };

  const removeConnection = (externalOrgUIID: string) => {
    setIsLoading(true);
    if (thirdParty) {
      const payload = {
        cproOrganizationId: organizationId,
        thirdPartyOrganizationId: externalOrgUIID,
        shouldRedirect: false,
        externalOrganizationName: thirdPartyName,
        externalRegistrationId: null
      };

      dispatch(postDataSharingConsent(payload));
    } else {
      dispatch(enqueueRequestErrorSnackbar('Partner ID does not exist'));
    }
    setIsLoading(false);
    setOpen(false);
  };

  return (
    <Paper classes={{ root: clsx(classes.container) }}>
      <Grid className={classes.contentContainer} sm={12}>
        <Grid sm={2}>{Logo ? <Logo data-testid={TestLocators.logo} /> : null}</Grid>
        <Grid className={classes.body} sm={10}>
          <Typography classes={{ root: clsx(classes.title) }} data-testid={TestLocators.thirdPartyName}>
            {thirdPartyName} {t('connections.integrationStatus', 'Integration Status')}:{' '}
            <b>{thirdParty ? dataSharingActive : <CircularProgress size={15} />}</b>
          </Typography>
          <Typography>
            {description}{' '}
            {thirdParty && thirdParty.dateConsentedUtc !== null ? (
              formatDate(thirdParty.dateConsentedUtc.toString())
            ) : (
              <CircularProgress variant={'indeterminate'} size={15} />
            )}
            <Tooltip title={toolTipText()} arrow placement="right-start">
              <IconButton aria-label="delete">
                <Info color={'primary'} />
              </IconButton>
            </Tooltip>
          </Typography>
        </Grid>
      </Grid>

      <Grid>
        {buttons.map(({ label }, index) => (
          <Button
            key={index}
            data-testid={TestLocators.localisation}
            variant="outlined"
            color="primary"
            onClick={() => setOpen(true)}
            classes={{ root: classes.button }}
            disabled={!thirdParty?.isDataSharing}
          >
            {isLoading ? <CircularProgress color="primary" size={24} /> : label}
          </Button>
        ))}
      </Grid>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('connections.removeIntegrationWith', 'Remove integration with')} {thirdPartyName}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" align={'center'}>
            {isLoading ? (
              <CircularProgress color="primary" size={24} />
            ) : (
              <Typography variant={'body1'}>
                {' '}
                {t(
                  'connections.stopSharing',
                  'Are you sure you wish to stop sharing your data with {{thirdPartyName}}?',
                  { thirdPartyName }
                )}
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined" color="primary">
            {t('membershipChange.dialog.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => removeConnection(id)} autoFocus variant="outlined" color="primary">
            {t('connections.removeIntegration', 'Remove integration')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
